import { useCallback, useEffect } from 'react';

import { MAIN_BRANCH_ID } from '@polyai/stream/constants';

import useBranches from 'hooks/agentStream/draft/useBranches';
import useCreateDraft from 'hooks/agentStream/draft/useCreateDraft';
import { useLocalDispatch } from 'hooks/reduxHooks';
import { useToast } from 'hooks/useToast';
import { setBranchId } from 'store/reducers/stream-tracker';
import { agentStreamLogger } from '../constants';
import type { ActionWithCommandId } from '../types';

const DEFAULT_BRANCH_NAME = 'Branch';

/**
 * Legacy hook for automatically creating a new branch when users make changes on the main branch.
 *
 * This hook implements the original single-draft behavior where any changes made on the main branch
 * would automatically create a new branch to isolate those changes. This was the behavior before
 * we introduced full multi-branch support.
 *
 * The hook is controlled by an `enabled` flag, allowing projects to opt into this legacy behavior
 * via feature flags while we transition to full multi-branch support.
 *
 * @deprecated This hook can be removed once we have full multi-branch support and no longer need
 * the feature flag to revert projects back to the single-branch behavior. At that point, users
 * will be expected to explicitly create branches when they want to make changes, rather than
 * having them auto-created.
 *
 * @return {{ isBranchError: boolean }} Whether there was an error creating the branch.
 */
const useAutoCreateBranch = ({
  enabled,
  accountId,
  projectId,
  branchId,
  actionQueue,
  lastKnownSequence,
}: {
  enabled: boolean;
  accountId: string;
  projectId: string;
  branchId: string;
  actionQueue: ActionWithCommandId[];
  lastKnownSequence: number;
}) => {
  const toast = useToast();
  const dispatch = useLocalDispatch();

  const {
    mutateAsync: createBranchAsync,
    isLoading: isCreatingBranch,
    isError: isBranchError,
  } = useCreateDraft();

  const { refetch: refetchBranches } = useBranches(projectId, enabled, {
    // Simply no other way, we are n levels deep into a race condition.
    // 1. race condition between merge being applied and then the branch getting deactivated
    // 2. race condition between react query and use effects
    // 3. also race condition with the hydration, but didnt dig into it anymore
    // TLDR; whenever we refetch branches, if there is a draft branch we open it
    // on merge this is just ignored so the setting to main works
    onSuccess: data => {
      // NOTE: Even though enabled is false, onSuccess can still fire because React Query
      // shares query state across hooks with the same queryKey. Other hooks (useWatchBranchId,
      // NewHeader, etc.) may have enabled=true, causing the query to run and this callback to fire.
      // We guard with the enabled prop to prevent unwanted side effects.
      if (!enabled) {
        return;
      }
      const nonMainBranch = data?.branches?.find(
        b => b.branchId !== MAIN_BRANCH_ID,
      );

      if (nonMainBranch) {
        dispatch(setBranchId({ branchId: nonMainBranch.branchId }));
      }
    },
  });

  const createBranch = useCallback(
    (lastKnownSequence: number) => {
      return createBranchAsync(
        {
          lastKnownSequence,
          branchName: DEFAULT_BRANCH_NAME,
        },
        {
          onSuccess: data => {
            dispatch(setBranchId({ branchId: data.branchId }));
          },
          onError: () => {
            toast.error({
              title: 'Error creating branch',
              description:
                'There was an error creating the branch, please refresh the page and try again.',
            });
          },
        },
      );
    },
    [createBranchAsync, dispatch, toast],
  );

  useEffect(() => {
    const maybeCreateBranch = async () => {
      if (
        enabled &&
        branchId === MAIN_BRANCH_ID &&
        actionQueue.length &&
        !isCreatingBranch &&
        !isBranchError &&
        lastKnownSequence !== undefined
      ) {
        const result = await refetchBranches();
        const nonMainBranch = result.data?.branches?.find(
          b => b.branchId !== MAIN_BRANCH_ID,
        );
        if (nonMainBranch) {
          dispatch(setBranchId({ branchId: nonMainBranch.branchId }));
        } else {
          agentStreamLogger?.info('Creating Draft', {
            accountId,
            projectId,
            branchId,
            actionQueue,
            lastKnownSequence,
          });
          void createBranch(lastKnownSequence);
        }
      }
    };

    void maybeCreateBranch();
  }, [
    enabled,
    accountId,
    projectId,
    actionQueue.length,
    createBranch,
    branchId,
    actionQueue,
    isCreatingBranch,
    isBranchError,
    lastKnownSequence,
    refetchBranches,
    dispatch,
  ]);

  return { isBranchError };
};

export default useAutoCreateBranch;
